import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import drEasyApi from '../Api/Customapi';

// Query keys
export const EQUIPMENTS_QUERY_KEY = ['equipments'];

// Fetch all equipments
export const useGetEquipments = () => {
    return useQuery({
        queryKey: EQUIPMENTS_QUERY_KEY,
        queryFn: async () => {
            const res = await drEasyApi.get('/api/Equipments/GetMyEquipments');
            return res.data || [];
        },
        onError: (err) => {
            if (err.response?.status === 403) {
                window.location.href = "/sub";
            } else {
                message.error('Failed to fetch equipments');
            }
        }
    });
};

// Add equipment mutation
export const useAddEquipment = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (values) => {
            return await drEasyApi.post('/api/Equipments/AddEquipment', {
                data: values
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(EQUIPMENTS_QUERY_KEY);
            message.success('Equipment added successfully');
        },
        onError: (err) => {
            if (err?.response?.status === 400) {
                const errorMessage = err?.response?.data?.message || 'Please check your input and try again';
                message.error(errorMessage);
            } else {
                message.error('Failed to add equipment');
            }
        }
    });
};

// Edit equipment mutation
export const useEditEquipment = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ id, values }) => {
            return await drEasyApi.put('/api/Equipments/EditEquipment', {
                id: id,
                data: values
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(EQUIPMENTS_QUERY_KEY);
            message.success('Equipment updated successfully');
        },
        onError: (err) => {
            if (err?.response?.status === 400) {
                const errorMessage = err?.response?.data?.message || 'Please check your input and try again';
                message.error(errorMessage);
            } else {
                message.error('Failed to update equipment');
            }
        }
    });
};

// Delete equipment mutation
export const useDeleteEquipment = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id) => {
            return await drEasyApi.delete('/api/Equipments/DeleteEquipment', { 
                params: { Id: id } 
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(EQUIPMENTS_QUERY_KEY);
            message.success('Equipment deleted successfully');
        },
        onError: () => {
            message.error('Failed to delete equipment');
        }
    });
};

// Upload PDF mutation
export const useUploadPDF = () => {
    return useMutation({
        mutationFn: async (file) => {
            const formData = new FormData();
            formData.append('File', file);
            const res = await drEasyApi.post('/api/Storage/UploadPDF', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                }
            });
            return res.data;
        },
        onError: () => {
            message.error('Failed to upload PDF');
        }
    });
};

// Delete PDF mutation
export const useDeletePDF = () => {
    return useMutation({
        mutationFn: async (id) => {
            return await drEasyApi.delete('/api/Storage/DeletePdf', { 
                params: { Id: id } 
            });
        },
        onError: () => {
            message.error('Failed to delete PDF');
        }
    });
};

// Add equipment document mutation
export const useAddEquipmentDoc = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (values) => {
            return await drEasyApi.post('/api/Equipments/AddEquipmentDoc', {
                data: values
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(EQUIPMENTS_QUERY_KEY);
            message.success('Document added successfully');
        },
        onError: () => {
            message.error('Failed to add document');
        }
    });
};

// Delete equipment document mutation
export const useDeleteEquipmentDoc = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (docId) => {
            return await drEasyApi.delete('/api/Equipments/DeleteEquipmentDoc', { 
                params: { DocId: docId } 
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(EQUIPMENTS_QUERY_KEY);
            message.success('Document deleted successfully');
        },
        onError: () => {
            message.error('Failed to delete document');
        }
    });
};
export const useUpdateMaintenanceDate = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ equipmentId, newMaintenanceDate }) => {
            return await drEasyApi.put('/api/Equipments/UpdateMaintenanceDate', {
                equipmentId,
                newMaintenanceDate
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(EQUIPMENTS_QUERY_KEY);
            message.success('Maintenance date updated successfully');
        },
        onError: () => {
            message.error('Failed to update maintenance date');
        }
    });
};

// Perform maintenance mutation
export const usePerformMaintenance = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (maintenanceData) => {
            return await drEasyApi.post('/api/Equipments/PerformMaintenance', {
                data: maintenanceData
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(EQUIPMENTS_QUERY_KEY);
            message.success('Maintenance performed successfully');
        },
        onError: () => {
            message.error('Failed to perform maintenance');
        }
    });
};

// Get maintenance history query
export const useGetMaintenanceHistory = (equipmentId) => {
    return useQuery({
        queryKey: ['maintenanceHistory', equipmentId],
        queryFn: async () => {
            const res = await drEasyApi.get(`/api/Equipments/${equipmentId}/MaintenanceHistory`);
            return res.data || [];
        },
        enabled: !!equipmentId,
        onError: () => {
            message.error('Failed to fetch maintenance history');
        }
    });
};

// Add issue mutation
export const useAddIssue = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ equipmentId, issueDetails }) => {
            return await drEasyApi.post('/api/Equipments/AddIssue', {
                equipmentId,
                issueDetails
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(EQUIPMENTS_QUERY_KEY);
            message.success('Issue reported successfully');
        },
        onError: () => {
            message.error('Failed to report issue');
        }
    });
};

// Get equipment issues query
export const useGetEquipmentIssues = (equipmentId) => {
    return useQuery({
        queryKey: ['equipmentIssues', equipmentId],
        queryFn: async () => {
            const res = await drEasyApi.get(`/api/Equipments/${equipmentId}/Issues`);
            return res.data || [];
        },
        enabled: !!equipmentId,
        onError: () => {
            message.error('Failed to fetch equipment issues');
        }
    });
};
